import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { PLATFORM_PAGES } from "../data/seoPages";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";

/**
 * SiteHeader
 *
 * Single sticky top-nav used on every page (/, /consulting,
 * /for-quick-commerce, /privacy, /terms, /404). The active audience is
 * detected from `location.pathname` and rendered as a non-clickable orange
 * marker so the nav cannot drift between pages.
 *
 * The trailing CTA behaviour is per-page:
 *   /             -> scroll to #book on the homepage
 *   /consulting   -> open the consulting Calendly link in a new tab
 *   others        -> navigate to / then let the homepage scroll take over
 *
 * All nav items carry stable `data-testid` attributes for E2E tests.
 */

const CONSULTING_CALENDLY =
  "https://calendar.app.google/q771ocWtwv6E75dr5";

const PLATFORMS_CALENDLY =
  "https://calendar.app.google/q771ocWtwv6E75dr5";

const FEEDS_URL = "https://feeds.letspickquick.com/";

const NavItem = ({ label, testid, onClick }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className="btn-ghost inline-flex items-center w-full md:w-auto text-left md:text-center justify-start md:justify-center"
  >
    {label}
  </button>
);

const ActiveMarker = ({ label, testid }) => (
  <span
    data-testid={testid}
    className="text-[var(--accent)] text-sm md:text-base font-semibold px-2"
  >
    {label}
  </span>
);

export const SiteHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const pathname = location.pathname || "/";
  const isEndToEnd = pathname === "/";
  const isConsulting = pathname === "/consulting";
  const isPlatforms = pathname === "/for-quick-commerce";

  const goEndToEnd = () => navigate("/");
  const goConsulting = () => navigate("/consulting");
  const goPlatforms = () => navigate("/for-quick-commerce");
  const openBlog = () => window.open(FEEDS_URL, "_blank", "noopener,noreferrer");

  const scrollOrGo = (id) => {
    if (pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const goContact = () => {
    if (pathname === "/consulting") {
      const el = document.getElementById("consulting-cta");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (pathname === "/") {
      scrollOrGo("contact");
    } else {
      // From /privacy, /terms, /for-quick-commerce, or /404: land on the
      // homepage contact section.
      navigate("/#contact");
    }
  };

  // Trailing CTA is per-page:
  //   /             -> scroll to #book on the homepage
  //   /consulting   -> Ferzin's Calendly (new tab)
  //   /for-quick-commerce -> Anuj's Calendly (new tab)
  //   /privacy, /terms, /404 -> scroll to homepage #book
  let ctaHref = null;
  if (isConsulting) ctaHref = CONSULTING_CALENDLY;
  else if (isPlatforms) ctaHref = PLATFORMS_CALENDLY;
  const ctaOnClick = ctaHref ? undefined : () => scrollOrGo("book");

  // eslint-disable-next-line react/no-unstable-nested-components
  const NavLinks = ({ onClick }) => (
    <>
      {isEndToEnd ? (
        <ActiveMarker label="End-to-End Ops" testid="nav-end-to-end-active" />
      ) : (
        <NavItem label="End-to-End Ops" testid="nav-end-to-end" onClick={() => { goEndToEnd(); onClick?.(); }} />
      )}
      {isConsulting ? (
        <ActiveMarker label="QC Consulting" testid="nav-consulting-active" />
      ) : (
        <NavItem label="QC Consulting" testid="nav-consulting" onClick={() => { goConsulting(); onClick?.(); }} />
      )}
      <NavItem label="Blog" testid="nav-blog" onClick={() => { openBlog(); onClick?.(); }} />
      <NavItem label="Contact" testid="nav-contact" onClick={() => { goContact(); onClick?.(); }} />

      {/* Platforms dropdown: exposes the 5 platform SEO pages on desktop.
          Uses a native <details> element so it works without JS and
          collapses cleanly on mobile. Kept last in the nav so the primary
          audience nav (End-to-End / Consulting / For Platforms) stays
          uncluttered. */}
      <details className="relative group hidden md:block" data-testid="nav-platforms-dropdown">
        <summary
          className="btn-ghost inline-flex items-center gap-1 cursor-pointer list-none"
          style={{ listStyle: "none" }}
        >
          Platforms
          <ChevronDown className="w-3.5 h-3.5" style={{ color: "#8A8A8A" }} />
        </summary>
        <div
          className="absolute right-0 mt-2 min-w-[280px] rounded-xl py-2 z-50"
          style={{ backgroundColor: "#0F0F0F", border: "1px solid #262626", boxShadow: "0 14px 40px rgba(0,0,0,0.5)" }}
        >
          {PLATFORM_PAGES.map((p) => (
            <a
              key={p.slug}
              href={`/${p.slug}`}
              className="block px-4 py-2 text-sm hover:bg-[var(--border)]"
              data-testid={`nav-platforms-item-${p.slug}`}
              style={{ color: "#B8B8B8", textDecoration: "none" }}
            >
              {p.label}
            </a>
          ))}
        </div>
      </details>
    </>
  );

  const ctaButtonStyle = {
    backgroundColor: "#F1592B",
    color: "#FFFFFF",
    height: "40px",
    padding: "0 16px",
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "-0.005em",
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
  };

  const renderCta = (mobile = false) => {
    const label = "Book a call";
    const styleWithWidth = mobile ? { ...ctaButtonStyle, width: "100%", justifyContent: "center" } : ctaButtonStyle;
    if (ctaHref) {
      return (
        <a
          href={ctaHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={mobile ? "mobile-book-call" : "header-book-call"}
          style={styleWithWidth}
          onClick={() => mobile && setMobileMenuOpen(false)}
        >
          {label}
        </a>
      );
    }
    return (
      <button
        type="button"
        data-testid={mobile ? "mobile-book-call" : "header-book-call"}
        onClick={() => { ctaOnClick?.(); mobile && setMobileMenuOpen(false); }}
        style={styleWithWidth}
      >
        {label}
      </button>
    );
  };

  return (
    <header className="sticky top-0 z-40 bg-[rgba(0,0,0,0.92)] backdrop-blur-xl">
      <div className="container flex items-center justify-between py-4">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex items-center gap-3"
          aria-label="Back to PickQuick homepage"
          data-testid="header-logo"
        >
          <img
            src="/pickquick-logo.png"
            alt="PickQuick Logo"
            className="h-9 w-auto object-contain"
          />
        </button>

        <nav className="hidden md:flex items-center gap-8 text-sm text-[var(--text-body)]">
          <NavLinks />
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {renderCta(false)}
        </div>

        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Open menu"
              className="md:hidden p-2 hover:bg-[var(--border)] rounded-lg transition-colors"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-full sm:max-w-md bg-[var(--bg-deep)] border-[var(--border)]"
          >
            <div className="flex flex-col gap-6 mt-8">
              <div className="flex flex-col gap-3 text-base text-[var(--text-body)]">
                <NavLinks onClick={() => setMobileMenuOpen(false)} />
              </div>
              <div className="flex flex-col gap-3 pt-4">
                {renderCta(true)}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default SiteHeader;
